/**
 * API Client for ADK Platform
 * Handles all HTTP requests with JWT authentication and tenant headers
 */

import axios, {
  type AxiosInstance,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from "axios";
import {
  getStoredToken,
  getStoredTenantId,
  removeStoredToken,
} from "@utils/storage";
import { isStudioSso } from "@/config/studio";
import type { ApiError } from "@/types";

// Create axios instance
// Production: Set VITE_API_URL to full backend URL including path (e.g., https://api.example.com/api/v1)
// Development: Leave unset - Vite dev server proxies /api/* to backend via VITE_DEV_API_TARGET
const api: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL || "/api/v1",
  timeout: 30000,
  // Send/receive the .graymatterlab.ai gml_sso cookie under studio_sso.
  // Harmless under hub_jwt (no cookie is set), so always on.
  withCredentials: true,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor - add auth token and tenant ID
api.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    // hub_jwt: attach the bearer token. studio_sso: auth rides on the
    // gml_sso cookie (sent via withCredentials), so no Authorization header.
    if (!isStudioSso()) {
      const token = getStoredToken();
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }

    // Tenant header is required in BOTH modes - the backend studio_sso path
    // fail-closes (401) unless X-Tenant-ID matches the mapped Studio org.
    const tenantId = getStoredTenantId();
    if (tenantId) {
      config.headers["X-Tenant-ID"] = tenantId;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  },
);

// Response interceptor - handle errors
api.interceptors.response.use(
  (response) => response,
  (error: AxiosError<ApiError>) => {
    // Handle 401 Unauthorized
    if (error.response?.status === 401) {
      // hub_jwt: clear the now-invalid bearer token. studio_sso: nothing to
      // clear client-side (the cookie is HttpOnly, owned by Studio).
      if (!isStudioSso()) {
        removeStoredToken();
      }
      // Dispatch event for auth context to handle
      window.dispatchEvent(new CustomEvent("auth:unauthorized"));
    }

    // Transform error for consistent handling
    const apiError: ApiError = error.response?.data ?? {
      type: "about:blank",
      title: "Network Error",
      status: error.response?.status ?? 0,
      detail: error.message || "An unexpected error occurred",
    };

    return Promise.reject(apiError);
  },
);

// Export the configured instance
export { api };

// Convenience methods
export const apiGet = <T>(url: string, params?: Record<string, unknown>) =>
  api.get<T>(url, { params }).then((res) => res.data);

export const apiPost = <T>(url: string, data?: unknown) =>
  api.post<T>(url, data).then((res) => res.data);

export const apiPatch = <T>(url: string, data?: unknown) =>
  api.patch<T>(url, data).then((res) => res.data);

export const apiPut = <T>(url: string, data?: unknown) =>
  api.put<T>(url, data).then((res) => res.data);

export const apiDelete = <T>(url: string, params?: Record<string, unknown>) =>
  api.delete<T>(url, { params }).then((res) => res.data);
